// ─────────────────────────────────────────────────────────────
// ToastContainer — renders the toast stack
// Mount once in App.js. Subscribes to toast events.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { subscribeToasts, toast as toastApi } from "../../lib/toast";
import { C } from "../../lib/colors";

const COLORS = {
  success: { bg: "#0c3a23", border: "#00e676", text: "#a8f7d0", icon: "✓" },
  error:   { bg: "#3a0c10", border: "#ff1744", text: "#ffb8c1", icon: "✕" },
  info:    { bg: "#0c1f3a", border: "#4f8ef7", text: "#bcd1ff", icon: "ℹ" },
};

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsub = subscribeToasts(event => {
      if (event.kind === "add") {
        setToasts(prev => [...prev, event.toast]);
        // Auto-dismiss after the toast's own duration
        const dur = event.toast.duration;
        if (dur > 0) {
          setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== event.toast.id));
          }, dur);
        }
      } else if (event.kind === "remove") {
        setToasts(prev => prev.filter(t => t.id !== event.id));
      }
    });
    return unsub;
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 380,
        pointerEvents: "none",
      }}
    >
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {toasts.map(t => {
        const c = COLORS[t.type] || COLORS.info;
        return (
          <div
            key={t.id}
            onClick={() => toastApi.dismiss(t.id)}
            style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderLeft: `3px solid ${c.border}`,
              borderRadius: 10,
              padding: "12px 16px",
              color: c.text,
              fontSize: 13,
              fontWeight: 500,
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              cursor: "pointer",
              pointerEvents: "auto",
              animation: "toast-in 0.2s ease-out",
              fontFamily: "inherit",
            }}
            role="status"
            aria-live="polite"
          >
            <span style={{ fontSize: 16, color: c.border, fontWeight: 700 }}>{c.icon}</span>
            <span style={{ flex: 1 }}>{t.message}</span>
          </div>
        );
      })}
    </div>
  );
}
